import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "./UI";
import { auth } from "../lib/api";
import { BarChart3, Star, ArrowRight } from "lucide-react";

/**
 * PersonalTopPages — dashboard widget showing THIS user's most-visited nav
 * routes based on the localStorage counter that Layout.jsx bumps on every
 * nav visit. Lets the user star routes they hit daily to pin them in the
 * sidebar Favorites — the whole loop is client-side and private.
 */
const NAV_LABEL_LOOKUP_URL_CACHE_KEY = "__tr_nav_index_cache__";

function readCounts(userId) {
  try {
    const raw = localStorage.getItem(`tr_route_counts_${userId || "anon"}`);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function readFavorites() {
  try {
    const raw = localStorage.getItem("tr_nav_favorites");
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function writeFavorites(next) {
  try {
    localStorage.setItem("tr_nav_favorites", JSON.stringify(next));
  } catch {
    /* no-op */
  }
}

/**
 * We need labels + icons for the paths we discover. Rather than duplicate the
 * NAV_GROUPS constant, Layout.jsx caches a compact { path: {label, group} }
 * map onto `window.__tr_nav_index_cache__` at mount. If that's not there yet
 * we fall back to showing the raw path — never a blocker.
 */
function getIndex() {
  return (typeof window !== "undefined" && window[NAV_LABEL_LOOKUP_URL_CACHE_KEY]) || {};
}

export function PersonalTopPages() {
  const navigate = useNavigate();
  const user = auth.getUser();
  const [tick, setTick] = useState(0);

  const counts = useMemo(() => readCounts(user?.id), [user?.id, tick]);
  const [favorites, setFavorites] = useState(() => readFavorites());
  const index = getIndex();

  useEffect(() => {
    // Refresh once a second while the widget is mounted — captures visits
    // recorded on other pages within the same tab/session.
    const t = setInterval(() => setTick((n) => n + 1), 3000);
    return () => clearInterval(t);
  }, []);

  const top = useMemo(() => {
    return Object.entries(counts)
      .filter(([k]) => !k.startsWith("__"))
      .filter(([k]) => index[k] || true)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5)
      .map(([path, visits]) => ({ path, visits, meta: index[path] }));
  }, [counts, index]);

  const totalVisits = top.reduce((s, r) => s + r.visits, 0);

  if (top.length === 0) {
    // Fresh user — nudge them to explore without shouting.
    return (
      <Card padded={false} data-testid="personal-top-pages-empty">
        <div className="px-5 py-4 flex items-center gap-3 text-sm text-slate-400">
          <BarChart3 className="w-4 h-4 text-cyan-300" />
          Your top pages will show up here after you visit a few sections — click around and I&apos;ll surface the shortcuts.
        </div>
      </Card>
    );
  }

  const togglePin = (path) => {
    const isFav = favorites.includes(path);
    const next = isFav ? favorites.filter((p) => p !== path) : [path, ...favorites].slice(0, 8);
    writeFavorites(next);
    setFavorites(next);
  };

  return (
    <Card padded={false} data-testid="personal-top-pages">
      <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 className="w-4 h-4 text-cyan-300" />
          <h3 className="font-display text-lg font-bold">Your most-visited pages</h3>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-slate-500">
          {totalVisits} visits · last 30 days
        </span>
      </div>
      <div className="divide-y divide-white/5">
        {top.map((row, i) => {
          const isFav = favorites.includes(row.path);
          const label = row.meta?.label || row.path;
          const group = row.meta?.group || "";
          const pct = totalVisits ? Math.round((row.visits / (top[0].visits || 1)) * 100) : 0;
          return (
            <div
              key={row.path}
              data-testid={`personal-top-row-${i}`}
              className="px-5 py-3 flex items-center gap-3 group hover:bg-white/[0.02] transition-colors"
            >
              <div className="w-6 text-center text-xs text-slate-500 font-mono">{i + 1}</div>
              <button
                onClick={() => navigate(row.path)}
                data-testid={`personal-top-link-${i}`}
                className="flex-1 text-left min-w-0"
              >
                <div className="text-sm text-white font-medium truncate">{label}</div>
                {group && (
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">
                    {group}
                  </div>
                )}
                <div className="mt-1.5 h-1 bg-white/5 rounded-full overflow-hidden max-w-xs">
                  <div
                    className="h-full bg-gradient-to-r from-cyan-400 to-blue-500"
                    style={{ width: `${pct}%` }}
                  />
                </div>
              </button>
              <div className="text-xs text-slate-400 font-mono w-14 text-right">
                {row.visits} <span className="text-slate-600">×</span>
              </div>
              <button
                onClick={() => togglePin(row.path)}
                data-testid={`personal-top-pin-${i}`}
                title={isFav ? "Unpin from favorites" : "Pin to favorites"}
                aria-label={isFav ? "Unpin" : "Pin"}
                className={`p-1.5 rounded-md transition-colors ${
                  isFav
                    ? "text-amber-300 hover:text-amber-100"
                    : "text-slate-500 opacity-0 group-hover:opacity-100 hover:text-amber-300"
                }`}
              >
                <Star className={`w-4 h-4 ${isFav ? "fill-amber-300" : ""}`} />
              </button>
              <button
                onClick={() => navigate(row.path)}
                aria-label="Open page"
                className="text-slate-500 hover:text-cyan-300 p-1.5"
              >
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
